/**
 * TopNav — horizontal top-bar navigation for the portal.
 *
 * Replaces SidebarNav / AppShell drawer layout. Matches the CRM98 reference
 * design:
 *   Left:   brand badge + product name + current user's display name
 *   Center: primary text nav links (Dashboard, Leads, Feedback, Users) or
 *           Dashboard + Org Search + Feedback + Website and Admin dropdowns
 *           for super_admin users
 *   Right:  user icon button → dropdown (Account Settings, Log out)
 *
 * Mobile (xs/sm): center nav links are hidden; a hamburger button opens a
 * temporary Drawer from the right containing all nav items.
 *
 * Admin dropdown (super_admin only):
 *   Trials         → /admin/trials
 *   Billing        → /admin/billing
 *   Super Admins   → /admin/super-admins
 *   Feedback Inbox → /feedback/inbox
 *
 * Active link detection mirrors the SidebarNav isItemActive logic:
 *   "/" exact only; "/leads" prefix; admin paths prefix.
 */
import { useEffect, useRef, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import HourglassBottomOutlinedIcon from '@mui/icons-material/HourglassBottomOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { Link, useLocation, useNavigate } from 'react-router';
import { BRAND } from '@crm98/shared';
import leadcraftIcon from '../../assets/leadcraft-icon.png';
import { useAuthStore } from '@/store/auth.js';
import { useLocationStore } from '@/store/location.js';
import { apiClient } from '@/lib/api-client.js';
import { queryClient } from '@/lib/query-client.js';
import { useCurrentUser } from '@/lib/api/account.js';
import { useLocations } from '@/hooks/useLocations.js';
import { groupLocationsByBusiness, shouldShowBusinessGroups } from '@/lib/group-locations.js';
import { useOrgFullSettings } from '@/lib/api/org-settings.js';
import { clearSuperAdminStash } from '@/auth/impersonation.js';

// ---------------------------------------------------------------------------
// Nav item definitions
// ---------------------------------------------------------------------------

interface NavLink {
  label: string;
  path: string;
  icon?: React.ReactNode;
  /** Match prefix instead of exact (default). Set to exact for root route. */
  exact?: boolean;
}

const REGULAR_NAV: NavLink[] = [
  { label: 'Dashboard', path: '/', exact: true, icon: <DashboardOutlinedIcon fontSize="small" /> },
  { label: 'Leads', path: '/leads', icon: <LeaderboardOutlinedIcon fontSize="small" /> },
  // PRD-020: website management — hidden for staff.
  { label: 'Website', path: '/website', icon: <LanguageOutlinedIcon fontSize="small" /> },
];

/** Website is the only flat-nav item staff can't see. */
const STAFF_HIDDEN_PATHS = new Set(['/website']);

/**
 * Org-level admin dropdown — mirrors the prototype's Admin nav (Team, Programs,
 * Settings, Integrations). Shown for org_owner / org_admin; hidden for staff
 * (dropdown not rendered) and super_admin (who has their own Admin dropdown).
 *
 * Prototype mapping:
 *   Team         → Users       (/users)
 *   Settings     → Organization (/org-settings)
 */
const ORG_ADMIN_NAV: NavLink[] = [
  { label: 'Users', path: '/users', icon: <PeopleOutlinedIcon fontSize="small" /> },
  { label: 'Settings', path: '/org-settings', icon: <BusinessOutlinedIcon fontSize="small" /> },
];

/**
 * Super-admin top-level links. Org Search is the team's primary tool, so it sits on
 * the bar itself rather than inside the Admin dropdown.
 */
const SUPER_ADMIN_NAV: NavLink[] = [
  { label: 'Dashboard', path: '/', exact: true, icon: <DashboardOutlinedIcon fontSize="small" /> },
  { label: 'Org Search', path: '/admin/orgs', icon: <BusinessOutlinedIcon fontSize="small" /> },
  { label: 'Feedback', path: '/feedback', icon: <FeedbackOutlinedIcon fontSize="small" /> },
];

const ADMIN_DROPDOWN: NavLink[] = [
  {
    label: 'Import Orgs',
    path: '/admin/org-import',
    icon: <UploadFileOutlinedIcon fontSize="small" />,
  },
  {
    label: 'Trials',
    path: '/admin/trials',
    exact: true,
    icon: <HourglassBottomOutlinedIcon fontSize="small" />,
  },
  {
    label: 'Billing',
    path: '/admin/billing',
    icon: <ReceiptLongOutlinedIcon fontSize="small" />,
  },
  {
    label: 'Super Admins',
    path: '/admin/super-admins',
    icon: <AdminPanelSettingsOutlinedIcon fontSize="small" />,
  },
  {
    label: 'Feedback Inbox',
    path: '/feedback/inbox',
    icon: <InboxOutlinedIcon fontSize="small" />,
  },
  {
    label: 'System Health',
    path: '/admin/health',
    icon: <MonitorHeartOutlinedIcon fontSize="small" />,
  },
  {
    label: 'Fix Queue',
    path: '/admin/fix-queue',
    icon: <BuildOutlinedIcon fontSize="small" />,
  },
  {
    label: 'R&D Backlog',
    path: '/admin/backlog',
    icon: <ScienceOutlinedIcon fontSize="small" />,
  },
];

/** Super-admin Website dropdown — parallels the non-admin /website page. */
const WEBSITE_DROPDOWN: NavLink[] = [
  { label: 'Builds', path: '/admin/builds', icon: <BuildOutlinedIcon fontSize="small" /> },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isLinkActive(link: NavLink, pathname: string): boolean {
  if (link.exact || link.path === '/') return pathname === link.path;
  return pathname === link.path || pathname.startsWith(`${link.path}/`);
}

function isAdminSectionActive(pathname: string): boolean {
  return ADMIN_DROPDOWN.some((link) => isLinkActive(link, pathname));
}

function isOrgAdminSectionActive(pathname: string): boolean {
  return ORG_ADMIN_NAV.some((link) => isLinkActive(link, pathname));
}

function isWebsiteSectionActive(pathname: string): boolean {
  return WEBSITE_DROPDOWN.some((link) => isLinkActive(link, pathname));
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** A single top-level text nav button, highlighted when active. */
function NavButton({ link }: { link: NavLink }) {
  const location = useLocation();
  const active = isLinkActive(link, location.pathname);

  return (
    <Button
      component={Link}
      to={link.path}
      disableRipple
      sx={{
        color: active ? '#0369a1' : 'text.secondary',
        fontWeight: active ? 500 : 400,
        fontSize: '0.875rem',
        textTransform: 'none',
        px: 1.5,
        py: 0.75,
        borderRadius: 2,
        bgcolor: active ? '#f0f9ff' : 'transparent',
        '&:hover': {
          bgcolor: active ? '#f0f9ff' : 'rgba(0,0,0,0.04)',
          // Only override color for inactive state (text.secondary → text.primary).
          // Active color '#0369a1' is unchanged on hover — no redundant spec.
          ...(active ? {} : { color: 'text.primary' }),
        },
      }}
    >
      {link.label}
    </Button>
  );
}

/** Shared dropdown nav button — renders a trigger button + Menu for a set of NavLinks. */
function NavDropdown({
  label,
  items,
  isActive,
}: {
  label: string;
  items: NavLink[];
  isActive: (pathname: string) => boolean;
}) {
  const location = useLocation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  const active = isActive(location.pathname);

  return (
    <>
      <Button
        disableRipple
        onClick={(e) => setAnchor(e.currentTarget)}
        endIcon={
          open ? (
            <KeyboardArrowUpIcon fontSize="small" />
          ) : (
            <KeyboardArrowDownIcon fontSize="small" />
          )
        }
        sx={{
          color: active ? '#0369a1' : 'text.secondary',
          fontWeight: active ? 500 : 400,
          fontSize: '0.875rem',
          textTransform: 'none',
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          bgcolor: active ? '#f0f9ff' : 'transparent',
          '&:hover': {
            bgcolor: active ? '#f0f9ff' : 'rgba(0,0,0,0.04)',
            // Only override color for inactive state (text.secondary → text.primary).
            // Active color '#0369a1' is unchanged on hover — no redundant spec.
            ...(active ? {} : { color: 'text.primary' }),
          },
        }}
      >
        {label}
      </Button>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        slotProps={{
          paper: {
            elevation: 2,
            sx: { mt: 0.5, minWidth: 180, borderRadius: 1.5 },
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {items.map((item) => {
          const itemActive = isLinkActive(item, location.pathname);
          return (
            <MenuItem
              key={item.path}
              component={Link}
              to={item.path}
              selected={itemActive}
              onClick={() => setAnchor(null)}
              sx={{ borderRadius: 1, mx: 0.5, px: 1.5 }}
            >
              <ListItemText
                primary={item.label}
                slotProps={{
                  primary: {
                    sx: {
                      fontWeight: itemActive ? 600 : 400,
                      color: itemActive ? 'primary.main' : 'inherit',
                    },
                  },
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

/** Super-admin dropdown — Import Orgs, Trials, Billing, Super Admins, Feedback Inbox, … */
function AdminDropdown() {
  return <NavDropdown label="Admin" items={ADMIN_DROPDOWN} isActive={isAdminSectionActive} />;
}

/** Super-admin Website dropdown — Builds (parallels non-admin /website page). */
function WebsiteDropdown() {
  return <NavDropdown label="Website" items={WEBSITE_DROPDOWN} isActive={isWebsiteSectionActive} />;
}

/** Org-level dropdown (Users + Settings) — visible to org_owner/org_admin. */
function OrgAdminDropdown() {
  return (
    <NavDropdown label="Organization" items={ORG_ADMIN_NAV} isActive={isOrgAdminSectionActive} />
  );
}

/** User icon button → dropdown with Account Settings and Log out. */
function UserMenu() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  // Prevents re-entrancy: a second click during the in-flight POST is ignored.
  const isLoggingOut = useRef(false);

  async function handleLogout() {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;
    setAnchor(null);
    try {
      // skipRefresh: true — an expired token should not trigger silentRefresh()
      // and issue a fresh session mid-teardown (P1 finding: logout POST 401 race).
      await apiClient.post('/v1/auth/logout', { skipRefresh: true });
    } catch {
      // Best-effort — clear local state regardless.
    } finally {
      isLoggingOut.current = false;
    }
    // clearSuperAdminStash before clearAuth so a stale impersonation stash in
    // sessionStorage cannot be picked up by a subsequent restoreSuperAdminToken()
    // call in the same tab (P1 finding: stash survives explicit logout).
    clearSuperAdminStash();
    clearAuth();
    await queryClient.cancelQueries();
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  return (
    <>
      <IconButton
        aria-label="user menu"
        onClick={(e) => setAnchor(e.currentTarget)}
        size="small"
        sx={{
          color: 'text.secondary',
          bgcolor: 'rgba(0,0,0,0.06)',
          width: 32,
          height: 32,
          borderRadius: '50%',
          '&:hover': { color: 'text.primary', bgcolor: 'rgba(0,0,0,0.10)' },
        }}
      >
        <PersonOutlinedIcon fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        slotProps={{
          paper: {
            elevation: 2,
            sx: { mt: 0.5, minWidth: 200, borderRadius: 1.5 },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {me?.email && (
          <>
            <Box sx={{ px: 2, pt: 1.25, pb: 0.75 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 220,
                }}
              >
                {me.email}
              </Typography>
            </Box>
            <Divider sx={{ mb: 0.5 }} />
          </>
        )}
        <MenuItem
          component={Link}
          to="/account-settings"
          onClick={() => setAnchor(null)}
          sx={{ borderRadius: 1, mx: 0.5, px: 1.5 }}
        >
          <ListItemIcon>
            <ManageAccountsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Account settings" />
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem onClick={() => void handleLogout()} sx={{ borderRadius: 1, mx: 0.5, px: 1.5 }}>
          <ListItemIcon>
            <LogoutOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Log out" />
        </MenuItem>
      </Menu>
    </>
  );
}

/**
 * Mobile nav drawer — slides in from the right when the hamburger is tapped.
 * Contains the full set of nav links for the current user's role.
 */
function MobileNavDrawer({
  open,
  onClose,
  isSuperAdmin,
  isStaff,
}: {
  open: boolean;
  onClose: () => void;
  isSuperAdmin: boolean;
  isStaff: boolean;
}) {
  const location = useLocation();

  const navItems = isSuperAdmin
    ? [...SUPER_ADMIN_NAV, ...WEBSITE_DROPDOWN, ...ADMIN_DROPDOWN]
    : [
        ...REGULAR_NAV.filter((link) => !(isStaff && STAFF_HIDDEN_PATHS.has(link.path))),
        ...(isStaff ? [] : ORG_ADMIN_NAV),
      ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{ '& .MuiDrawer-paper': { width: 240, pt: 1 } }}
    >
      <List component="nav" sx={{ px: 1 }}>
        {navItems.map((item) => {
          const active = isLinkActive(item, location.pathname);
          return (
            <ListItemButton
              key={item.path}
              component={Link}
              to={item.path}
              selected={active}
              onClick={onClose}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                slotProps={{
                  primary: {
                    sx: { fontWeight: active ? 600 : 400 },
                  },
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// BOLT-166: Org badge with integrated location selector
// ---------------------------------------------------------------------------

/**
 * Left-side header element: brand badge + org name + location subtitle.
 * For multi-location orgs (2+), clicking opens a dropdown to switch locations.
 * Single-location orgs show the org name only, no dropdown.
 * Super admins see the platform brand name (no org context).
 */
function OrgBadge({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  const orgId = useAuthStore((s) => s.orgId);
  const { data: orgData } = useOrgFullSettings(orgId ?? '', !isSuperAdmin && !!orgId);
  // Super admins have no org, so /v1/locations rejects them — an ungated fetch
  // here 401-stormed the silent-refresh path on every page view (2026-09-10).
  const { data: locationsData } = useLocations({ enabled: !isSuperAdmin });
  const selectedLocationId = useLocationStore((s) => s.selectedLocationId);
  const setSelectedLocationId = useLocationStore((s) => s.setSelectedLocationId);

  // Resolve the org logo URL — S3 keys need a presigned preview fetch.
  // logoResolved tracks whether we've finished determining the logo state so we
  // don't flash the "97" fallback badge while org data is still loading.
  const rawLogoUrl = orgData?.settings?.design?.logo_url;
  const [displayLogoUrl, setDisplayLogoUrl] = useState('');
  const [logoResolved, setLogoResolved] = useState(isSuperAdmin);
  // Reset resolution state when the org context changes so the grey placeholder
  // re-appears while the incoming org's logo resolves (e.g. admin org switching).
  useEffect(() => {
    if (!isSuperAdmin) setLogoResolved(false);
  }, [orgId, isSuperAdmin]);
  useEffect(() => {
    // Super admins have no org context — nothing to resolve.
    if (isSuperAdmin) return;
    // Org data hasn't arrived yet; stay unresolved so we don't show the "97" badge.
    if (!orgData) return;

    if (!rawLogoUrl) {
      setDisplayLogoUrl('');
      setLogoResolved(true);
      return;
    }
    if (!rawLogoUrl.startsWith('s3://')) {
      setDisplayLogoUrl(rawLogoUrl);
      setLogoResolved(true);
      return;
    }
    if (!orgId) {
      setLogoResolved(true);
      return;
    }
    let cancelled = false;
    const s3Key = rawLogoUrl.replace(/^s3:\/\/[^/]+\//, '');
    apiClient
      .post<{ preview_url: string }>(`/v1/orgs/${orgId}/uploads/preview`, {
        body: { s3_key: s3Key },
      })
      .then((res) => {
        if (!cancelled) {
          setDisplayLogoUrl(res.preview_url);
          setLogoResolved(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDisplayLogoUrl('');
          setLogoResolved(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rawLogoUrl, orgId, orgData, isSuperAdmin]);

  const orgName = orgData?.name ?? BRAND.productName;
  const locations = locationsData?.locations ?? [];
  const isMultiLocation = locations.length >= 2;

  // A user with NO accessible locations is a real, reachable state — a business
  // created without a location, or an admin assigned only to such a business.
  // It must not render like a healthy single-location studio: every
  // location-scoped page below is empty, and without a subtitle here there is
  // nothing anywhere in the shell to explain why. Only claim it once the query
  // has actually resolved, so the header does not accuse a loading page.
  const hasNoLocations = !isSuperAdmin && locationsData !== undefined && locations.length === 0;

  const locationGroups = groupLocationsByBusiness(locations);
  const showBusinessGroups = shouldShowBusinessGroups(locationGroups);

  // Validate stored selection — clear stale IDs
  const validSelection = selectedLocationId && locations.some((l) => l.id === selectedLocationId);
  const effectiveId = validSelection ? selectedLocationId : null;
  if (selectedLocationId && !validSelection) {
    setSelectedLocationId(null);
  }

  const subtitle = isSuperAdmin
    ? null
    : hasNoLocations
      ? 'No locations'
      : isMultiLocation
        ? effectiveId
          ? (locations.find((l) => l.id === effectiveId)?.name ?? 'Location')
          : 'All Locations'
        : null;

  const clickable = !isSuperAdmin && isMultiLocation;

  return (
    <>
      <Box
        onClick={clickable ? (e) => setAnchor(e.currentTarget) : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          cursor: clickable ? 'pointer' : 'default',
          borderRadius: 2,
          px: 0.5,
          py: 0.25,
          mr: 0.5,
          '&:hover': clickable ? { bgcolor: 'rgba(0,0,0,0.04)' } : {},
        }}
      >
        {/* Org logo — grey placeholder while loading, the LeadCraft icon only
            once we know no logo exists, actual org logo image when resolved. */}
        {displayLogoUrl ? (
          <Box
            component="img"
            src={displayLogoUrl}
            alt=""
            sx={{
              height: 32,
              maxWidth: 80,
              objectFit: 'contain',
              flexShrink: 0,
              borderRadius: 1,
            }}
          />
        ) : !logoResolved ? (
          <Box
            sx={{
              width: 32,
              height: 32,
              bgcolor: 'grey.200',
              borderRadius: 1.5,
              flexShrink: 0,
            }}
          />
        ) : (
          <Box
            component="img"
            src={leadcraftIcon}
            alt={BRAND.productName}
            sx={{ height: 32, width: 'auto', flexShrink: 0 }}
          />
        )}

        <Box sx={{ lineHeight: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '0.875rem',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: { xs: 120, sm: 180 },
              }}
            >
              {orgName}
            </Typography>
            {clickable && (
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: '1rem',
                  color: 'text.secondary',
                  transition: 'transform 0.15s',
                  transform: open ? 'rotate(180deg)' : 'none',
                }}
              />
            )}
          </Box>
          {subtitle && (
            <Typography
              variant="caption"
              // Colour goes through `sx`, NOT the `color` prop: MUI removed the
              // system `color` prop from Typography in v7 (this repo is on v9),
              // so `color="text.secondary"` here silently emitted no declaration
              // at all — the subtitle has been rendering as text.primary. Caught
              // by reading the generated class on a live page, not by any test.
              sx={{
                lineHeight: 1.2,
                display: 'block',
                color: hasNoLocations ? 'warning.main' : 'text.secondary',
                fontWeight: hasNoLocations ? 600 : 400,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {clickable && (
        <Menu
          anchorEl={anchor}
          open={open}
          onClose={() => setAnchor(null)}
          slotProps={{
            paper: {
              elevation: 2,
              sx: { mt: 0.5, minWidth: 220, maxWidth: 300, borderRadius: 1.5 },
            },
          }}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        >
          <MenuItem
            onClick={() => {
              setSelectedLocationId(null);
              setAnchor(null);
            }}
            selected={effectiveId === null}
            sx={{ borderRadius: 1, mx: 0.5, px: 1.5 }}
          >
            <ListItemText primary="All Locations" />
            {effectiveId === null && (
              <CheckIcon fontSize="small" sx={{ ml: 1, color: 'primary.main' }} />
            )}
          </MenuItem>

          <Divider sx={{ my: 0.5 }} />

          {locationGroups.map(({ businessId, businessName, items }) => [
            // Only label the owning business when there is more than one, so
            // single-business studios (the common case) render exactly as before.
            // Without this a franchise sees one flat pool and cannot tell which
            // legal entity a location belongs to — two businesses can easily
            // hold similarly-named locations in the same metro.
            showBusinessGroups ? (
              <ListSubheader
                key={`sub-${businessId}`}
                disableSticky
                sx={{
                  bgcolor: 'transparent',
                  lineHeight: 2,
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  px: 2,
                }}
              >
                {businessName}
              </ListSubheader>
            ) : null,
            ...items.map((loc) => (
              <MenuItem
                key={loc.id}
                onClick={() => {
                  setSelectedLocationId(loc.id);
                  setAnchor(null);
                }}
                selected={effectiveId === loc.id}
                sx={{ borderRadius: 1, mx: 0.5, px: 1.5 }}
              >
                <ListItemText
                  primary={loc.name ?? 'Unnamed'}
                  secondary={loc.city && loc.state ? `${loc.city}, ${loc.state}` : undefined}
                />
                {effectiveId === loc.id && (
                  <CheckIcon fontSize="small" sx={{ ml: 1, color: 'primary.main' }} />
                )}
              </MenuItem>
            )),
          ])}
        </Menu>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// TopNav
// ---------------------------------------------------------------------------

export function TopNav() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const role = useAuthStore((s) => s.role);
  const isSuperAdmin = role === 'super_admin';
  const isStaff = role === 'staff';

  return (
    <>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{ borderBottom: '1px solid', borderColor: 'divider', zIndex: (t) => t.zIndex.appBar }}
      >
        <Toolbar
          sx={{
            gap: 1,
            minHeight: { xs: 56, sm: 56 },
            maxWidth: 896,
            mx: 'auto',
            width: '100%',
            px: { xs: 2, sm: 2 },
          }}
        >
          {/* ── Left: org badge with integrated location selector (BOLT-166) ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <OrgBadge isSuperAdmin={isSuperAdmin} />
          </Box>

          {/* ── Center: nav links — hidden on mobile, shown on md+ ───────────── */}
          <Box
            component="nav"
            aria-label="primary navigation"
            sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}
          >
            {isSuperAdmin ? (
              <>
                {SUPER_ADMIN_NAV.map((link) => (
                  <NavButton key={link.path} link={link} />
                ))}
                <WebsiteDropdown />
                <AdminDropdown />
              </>
            ) : (
              <>
                {REGULAR_NAV.filter((link) => !(isStaff && STAFF_HIDDEN_PATHS.has(link.path))).map(
                  (link) => (
                    <NavButton key={link.path} link={link} />
                  ),
                )}
                {!isStaff && <OrgAdminDropdown />}
              </>
            )}
          </Box>

          {/* ── Right: user icon + hamburger (hamburger only on mobile) ──────── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flex: 1,
              justifyContent: 'flex-end',
            }}
          >
            <UserMenu />
            {isMobile && (
              <IconButton
                aria-label="open navigation menu"
                onClick={() => setDrawerOpen(true)}
                size="small"
                sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
              >
                <MenuOutlinedIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile nav drawer — only mounted on mobile viewports */}
      <MobileNavDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        isSuperAdmin={isSuperAdmin}
        isStaff={isStaff}
      />
    </>
  );
}
